import type { Graph } from '@/schema/estate';

/** Why `prefix` can't number a new group in the estate, or '' when it can. */
export function prefixProblem(g: Graph, estateId: string, prefix: string): string {
  const want = prefix.trim().toUpperCase();
  if (!/^[A-Z]{1,4}$/.test(want)) return 'A prefix is 1 to 4 letters, A to Z.';
  const taken = listItemGroups(g, estateId).find((r) => r.prefix.toUpperCase() === want);
  return taken ? `${want} is already the prefix of ${taken.name}.` : '';
}

/** The prefix is fixed at creation: numbers already issued under it depend on it. */
export async function createItemGroup(
  g: Graph,
  opts: { estateId: string; name: string; prefix: string; groupId: string; author: string },
): Promise<string> {
  const name = opts.name.trim();
  if (!name) throw new Error('A group needs a name.');
  const problem = prefixProblem(g, opts.estateId, opts.prefix);
  if (problem) throw new Error(problem);
  const id = crypto.randomUUID();
  const now = Date.now();
  const siblings = listItemGroups(g, opts.estateId);
  await g.item_group.create({
    id,
    owner_group_id: opts.groupId,
    estate_id: opts.estateId,
    name,
    prefix: opts.prefix.trim().toUpperCase(),
    sort_order: siblings.length,
    active: true,
    created_at: now,
    updated_at: now,
    archived: false,
  });
  return id;
}

export function listItemGroups(g: Graph, estateId: string) {
  return g.item_group
    .all()
    .filter((r) => r.estate_id === estateId && !r.archived)
    .sort((a, b) => a.sort_order - b.sort_order);
}

export function defaultPrefix(name: string): string {
  return (name.replace(/[^A-Za-z]/g, '').slice(0, 2) || 'XX').toUpperCase();
}

export async function renameItemGroup(g: Graph, id: string, name: string): Promise<void> {
  await g.item_group.update(id, { name, updated_at: Date.now() });
}

export async function archiveItemGroup(g: Graph, id: string): Promise<void> {
  await g.item_group.update(id, { archived: true, active: false, updated_at: Date.now() });
}
